import React, { createRef, SyntheticEvent, useState } from 'react';
import { Copy, Trash } from 'react-feather';
import { useTranslation } from 'react-i18next';
import { useHistory, useRouteMatch } from 'react-router-dom';
import {
  faEllipsisV,
  faExclamationTriangle,
  faSearch,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Dropdown,
  Empty,
  Input,
  Menu,
  Modal,
  notification,
  Space,
  Spin,
  Tooltip,
} from 'antd';
import { ColumnsType } from 'antd/es/table';
import { ModalFuncProps } from 'antd/lib/modal';
import { TFunction } from 'i18next';
import Button from 'jsx/common/Button';
import Fetch from 'jsx/common/util/_fetch';
import isEmpty from 'lodash/isEmpty';
import {
  ApiAutomation,
  CompactTable,
  Flex,
  insertArrayIf,
  objectArraySort,
  openNotificationWithIcon,
  PageContent,
  Switch,
  useFetch,
  useUserinfo,
} from '@mss/mss-component-library';
import StatusIcon from 'jsx/common/StatusIcon';
import { useConfigurationQuery } from '../../../index';
import classNames from 'classnames';

const filterDropdown =
  (
    t: TFunction,
    handleSearch: (selectedKeys: string[], confirm: Function) => void,
    handleReset: (clearFilters: Function) => void
  ) =>
  ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => {
    const inputRef = createRef<any>();
    return (
      <div style={{ padding: 8 }}>
        <Input
          ref={inputRef}
          placeholder={t('Search name')}
          data-ta="SearchNameInput"
          value={selectedKeys[0]}
          onChange={e =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm)}
          style={{ width: 188, marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            variant="primary"
            onClick={() => handleSearch(selectedKeys, confirm)}
            size="small"
            style={{ width: 90 }}
            data-ta="SearchButton"
          >
            {t('Search')}
          </Button>
          <Button
            onClick={() => handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
            data-ta="ResetButton"
          >
            {t('Reset')}
          </Button>
        </Space>
      </div>
    );
  };

export const MenuItem = ({
  className,
  ...props
}: React.ComponentProps<typeof Menu.Item>) => {
  return (
    <Menu.Item
      className={classNames(
        'py-2 px-6 [&:not(:last-child)]:border-b [&:not(:last-child)]:border-gray-200',
        className
      )}
      {...props}
    />
  );
};

type Columns = (
  t: TFunction,
  hasPermission: (codename: string | string[]) => boolean,
  handleSearch: (selectedKeys: string[], confirm: Function) => void,
  handleReset: (clearFilters: Function) => void,
  toggleAutomation: (enable: boolean, id: ApiAutomation) => void,
  toggleDebug: (enable: boolean, id: ApiAutomation) => void,
  deleteModal: (automation: ApiAutomation) => Promise<{
    destroy: () => void;
    update: (newConfig: ModalFuncProps) => void;
  }>,
  showLogs: (event: SyntheticEvent, automationId: string) => void,
  loading: boolean,
  copyAutomation: any,
  user: any,
  unidirectional?: boolean
) => ColumnsType<ApiAutomation>;

const columns: Columns = (
  t,
  hasPermission,
  handleSearch,
  handleReset,
  toggleAutomation,
  toggleDebug,
  deleteModal,
  showLogs,
  loading,
  copyAutomation,
  user,
  unidirectional
) => [
  {
    title: t('Name'),
    key: 'name',
    dataIndex: 'name',
    sorter: (a: ApiAutomation, b: ApiAutomation) =>
      objectArraySort(a, b, 'name'),
    filterDropdown: filterDropdown(t, handleSearch, handleReset),
    filterIcon: (filtered: boolean) => (
      <Flex h="center" v="center" style={{ height: '100%' }}>
        <FontAwesomeIcon
          color={filtered ? '#417e80' : undefined}
          icon={faSearch}
        />
      </Flex>
    ),
    onFilter: (value, record) =>
      record.name && typeof value === 'string'
        ? record.name.toString().toLowerCase().includes(value.toLowerCase())
        : false,
  },
  {
    title: t('Debug'),
    dataIndex: 'debug',
    key: 'debug',
    width: 50,
    render: (debug: boolean, automation: ApiAutomation) => (
      <Flex
        h="center"
        v="center"
        onClick={e => {
          e.stopPropagation();
        }}
      >
        <Switch
          size="small"
          isChecked={debug}
          disabled={loading || !hasPermission('change_automation')}
          onChange={() => toggleDebug(!debug, automation)}
        />
      </Flex>
    ),
  },
  {
    title: t('Status'),
    dataIndex: 'enabled',
    key: 'enabled',
    width: 50,
    render: (enabled: boolean, automation: ApiAutomation) => (
      <Flex
        h="center"
        v="center"
        onClick={e => {
          e.stopPropagation();
        }}
      >
        <Switch
          size="small"
          isChecked={enabled}
          disabled={loading || !hasPermission('change_automation')}
          onChange={() => toggleAutomation(!enabled, automation)}
        />
      </Flex>
    ),
  },
  ...insertArrayIf(!unidirectional, [
    {
      title: t('Log'),
      width: 50,
      render: (automation: any) =>
        !isEmpty(automation.error) ? (
          <Tooltip title={automation.error.message}>
            <Flex
              h="center"
              v="center"
              onClick={(event: SyntheticEvent) =>
                showLogs(event, automation.id)
              }
            >
              <StatusIcon
                enabled={automation.enabled && isEmpty(automation.error)}
                error={automation.enabled && !isEmpty(automation.error)}
              />
            </Flex>
          </Tooltip>
        ) : (
          <Flex
            h="center"
            v="center"
            onClick={(event: SyntheticEvent) => showLogs(event, automation.id)}
          >
            <StatusIcon
              enabled={automation.enabled && isEmpty(automation.error)}
              error={automation.enabled && !isEmpty(automation.error)}
            />
          </Flex>
        ),
    },
  ]),
  {
    title: '',
    key: 'actions',
    dataIndex: 'id',
    width: 16,
    render: (id: string, automation) => {
      /*
        When the user is no superuser, can delete and the item is not a system
        automation show the delete option
        OR
        When superuser, all is allowed
         */
      const can_delete =
        (!user.is_superuser &&
          hasPermission('delete_automation') &&
          automation.system_id === null) ||
        user.is_superuser;
      const can_copy = hasPermission(['add_automation']);

      if (!can_delete && !can_copy) {
        return;
      }

      return (
        <div
          onClick={e => {
            e.stopPropagation();
          }}
        >
          {(can_delete || can_copy) && (
            <Dropdown
              trigger={['click']}
              overlay={
                <Menu>
                  {can_delete && (
                    <MenuItem onClick={() => deleteModal(automation)}>
                      <Flex v="center">
                        <Trash size={14} className="mr-2" />
                        {t('Delete')}
                      </Flex>
                    </MenuItem>
                  )}
                  {can_copy && (
                    <MenuItem
                      hidden={false}
                      onClick={() => copyAutomation(automation)}
                    >
                      <Flex v="center">
                        <Copy size={14} className="mr-2" />
                        {t('Copy')}
                      </Flex>
                    </MenuItem>
                  )}
                </Menu>
              }
            >
              <div style={{ width: 32, textAlign: 'center' }}>
                <FontAwesomeIcon icon={faEllipsisV} size="sm" />
              </div>
            </Dropdown>
          )}
        </div>
      );
    },
  },
];

function Automations() {
  const [t] = useTranslation(['configuration', 'common', 'authorization']);
  const history = useHistory();
  const { url } = useRouteMatch();
  const [hasPermission, , user] = useUserinfo();
  const { data: config } = useConfigurationQuery();

  const [, setSearch] = useState('');
  const [{ data: automations, loading }, fetchAutomations] = useFetch<
    ApiAutomation[]
  >({
    url: `/api/automation/`,
    defaultValue: [],
  });

  const handleSearch = (selectedKeys: string[], confirm: Function) => {
    confirm();
    setSearch(selectedKeys[0]);
  };

  const handleReset = (clearFilters: Function) => {
    clearFilters();
    setSearch('');
  };

  const copyAutomation = async (automation: ApiAutomation) => {
    try {
      const result = await Fetch.post(`/api/automation/${automation.id}/copy/`);
      if (result.status === 201) {
        openNotificationWithIcon(
          notification.success,
          t(`Automation copied`),
          ''
        );
        const data = await result.json();

        history.push({
          pathname: `${url}/${data['id']}`,
          state: { type: 'copy' },
        });
      } else {
        throw new Error();
      }
    } catch {
      openNotificationWithIcon(
        notification.error,
        t(`Automation copy failed`),
        ''
      );
    }
  };

  const deleteModal = async (automation: ApiAutomation) =>
    Modal.confirm({
      title: t('Are you sure you want to delete?'),
      icon: (
        <span className="anticon">
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            color="#fd9644"
            size="sm"
          />
        </span>
      ),
      content: (
        <div>
          <blockquote>{automation.name}</blockquote>
          <p>{t(`You're about to delete this automation.`)}</p>
        </div>
      ),
      onOk: async () => {
        try {
          const result = await Fetch.delete(
            `/api/automation/${automation.id}/`
          );
          if (result.status === 204) {
            openNotificationWithIcon(
              notification.success,
              t(`Automation deleted`),
              ''
            );
            fetchAutomations();
          } else {
            throw new Error();
          }
        } catch {
          openNotificationWithIcon(
            notification.error,
            t(`Automation deletion failed`),
            ''
          );
        }
      },
      okButtonProps: { danger: true },
    });

  const toggleAutomation = async (
    enabled: boolean,
    automation: ApiAutomation
  ) => {
    try {
      const resp = await Fetch.patch(`/api/automation/${automation.id}/`, {
        body: JSON.stringify({ enabled }),
      });
      if (resp.status === 200) {
        openNotificationWithIcon(
          enabled ? notification.success : notification.warning,
          enabled
            ? t('Automation enabled')
            : t('Automation disabled'),
          automation.name
        );
        fetchAutomations();
      } else {
        throw new Error();
      }
    } catch {
      openNotificationWithIcon(
        notification.error,
        t('Automation toggling failed'),
        automation.name
      );
    }
  };
  const toggleDebug = async (enabled: boolean, automation: ApiAutomation) => {
    try {
      const resp = await Fetch.patch(`/api/automation/${automation.id}/`, {
        body: JSON.stringify({ debug: enabled }),
      });
      if (resp.status === 200) {
        openNotificationWithIcon(
          enabled ? notification.success : notification.warning,
          enabled
            ? t('Debug enabled')
            : t('Debug disabled'),
          automation.name
        );
        fetchAutomations();
      } else {
        throw new Error();
      }
    } catch {
      openNotificationWithIcon(
        notification.error,
        t('Automation toggling failed'),
        ''
      );
    }
  };

  const showLogs = (event: SyntheticEvent, automationId: string) => {
    event.stopPropagation();
    history.push(`${url}/${automationId}/log`);
  };

  return (
    <PageContent
      title={t('Automations')}
      actions={
        <Flex v="center">
          {hasPermission(['add_automation']) && (
            <Button
              onClick={() => history.push(`${url}/add`)}
              data-ta="addAssetButton"
            >
              {t('ADD AUTOMATION')}
            </Button>
          )}
        </Flex>
      }
    >
      {loading && automations.length === 0 && (
        <Flex v="center" h="center">
          <Spin />
        </Flex>
      )}

      {automations.length > 0 && (
        <CompactTable
          rowKey="id"
          onRow={(record: any) => {
            return {
              onClick: () =>
                history.push({
                  pathname: `${url}/${record.id}`,
                  state: { type: 'edit' }, 
                }),
            };
          }}
          pagination={{
            style: { marginLeft: 0 },
            showTotal: (total: number, range: any[]) =>
              t('(RANGE1)-(RANGE2) of (TOTAL) items', {
                range1: range[0],
                range2: range[1],
                TOTAL: total,
              }),
          }}
          dataSource={automations}
          columns={columns(
            t,
            hasPermission,
            handleSearch,
            handleReset,
            toggleAutomation,
            toggleDebug,
            deleteModal,
            showLogs,
            loading,
            copyAutomation,
            user,
            config?.unidirectional
          )}
        />
      )}
      {automations.length === 0 && !loading && (
        <Empty
          description={t(
            `configuration:There aren't any automations yet for this site.`
          )}
        />
      )}
    </PageContent>
  );
}

export default Automations;
